import { createStyles, Divider, Link, makeStyles, Theme, Typography } from "@material-ui/core";
import * as React from "react";
import { getOriginalGamePowerupImageName, OriginalGamePowerup } from "../GameContext/OriginalGameState";
import { useWindowDimensions } from "../UI/UseWindowDimensions";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            margin: "auto",
            marginBottom: "4rem",
            marginTop: "3rem",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            gap: "0.5rem",
            maxWidth: 800
        },
        textFlex: {
            display: "flex",
            alignItems: "center",
            justifyContent: "left",
            gap: "0.5rem"
        },
        powerupIcon: {
            backgroundColor: "#b7b4ae",
            borderRadius: "30px",
            lineHeight: 0,
            padding: "5px"
        }
    })
);

interface PowerupInfo {
    powerup: OriginalGamePowerup;
    alt: string;
    info: string;
}

const POWERUP_INFO_LIST: PowerupInfo[] = [
    {
        powerup: OriginalGamePowerup.ScoreMultiplier,
        alt: "Score multiplier powerup icon",
        info: "Score multiplier: apply a stackable 2x score multiplier to yourself. The multiplier affects score while the multiplier is active and a word is correctly guessed, you use all guesses in the word, or when the game ends and the last guess you made was during the multipier."
    },
    {
        powerup: OriginalGamePowerup.Hint,
        alt: "Hint powerup icon",
        info: "Hint: receive a green letter that you have not yet correctly guessed."
    },
    {
        powerup: OriginalGamePowerup.ColorFlip,
        alt: "Color flip powerup icon",
        info: "Color flip: pick a target and flip the colors for a random guess row in the target's current word."
    },
    {
        powerup: OriginalGamePowerup.HideGuess,
        alt: "Hide guess powerup icon",
        info: "Hide guess: pick a target and conceal an entire random guess row in the target's current word."
    }
]

export const AboutPage = () => {
    const classes = useStyles();
    const { width, height } = useWindowDimensions();

    return (
        <div className={classes.root}>
            <Typography variant="h2"><b>About</b></Typography>
            <Typography variant="h6">
                This is a project independently managed and developed by me, <Link color="secondary" target="_blank" href="https://evanthompson.site/">Evan</Link>.
                I wanted to create a fun multiplayer spin-off of popular word games, which is why I created Word Arena!
                Word Arena is and always will be free, and I hope you can enjoy this game with your friends (or even by yourself!) as much as I have.
            </Typography>

            <div style={{ height: "4rem" }} />

            <Typography variant="h2"><b>How to Play</b></Typography>
            <Typography variant="h4">Core Gameplay</Typography>
            <Typography variant="h6">
                The original game mode is simple at its core, but there are some added elements to throw in a bit of complexity. It can be played with one or many players.
                The image below explains it well:
            </Typography>
            <img src="images/help-original.png" alt="Original game help" width={Math.min(width * 0.9, 600)} />
            <Typography variant="h6">
                A green letter indicates the letter and position are correct, while a yellow letter indicates the letter exists somewhere else in the word.
                If you use up all of your guesses without guessing the word, your score will increase by the 'W' (word) bonus, which is calculated
                based on how close your last guess was to the actual word. A correct solution will award you with a base quantity of points minus a penalty for each additional guess. Once you complete all words in a timed game, you will recieve a bonus calculated
                based on the amount of words you were able to correctly guess and the time you have remaining.
            </Typography>

            <div style={{ height: "2rem" }} />
            <Typography variant="h4">Powerups</Typography>
            <Typography variant="h6">
                When playing the original game mode, there is an option to play with powerups. Each player starts with an equivalent delay before their first powerup,
                which is displayed via this large timer:
            </Typography>
            <img src="images/help-powerups1.png" alt="Powerups help 1" width={Math.min(width * 0.9, 200)} />
            <Typography variant="h6">
                Once the timer runs out, each player will be given a random powerup which can then be used. Once the player uses their powerup, there is a delay before they can use another.
            </Typography>
            <table style={{ textAlign: "left", borderSpacing: "0.5rem 0.5rem" }}>
                <tbody>
                    {
                        POWERUP_INFO_LIST.map((e, i) => {
                            return (
                                <tr key={i}>
                                    <td className={classes.powerupIcon}>
                                        <img src={`images/powerups/${getOriginalGamePowerupImageName(e.powerup)}`} alt={e.alt} width={48} />
                                    </td>
                                    <td>
                                        <Typography variant="h6">{e.info}</Typography>
                                    </td>
                                </tr>
                            )
                        })
                    }
                </tbody>
            </table>
        </div>
    );
}